/**
 * shader.js - Handles shader loading and parameter management for SoftPresence
 */

// Define shader parameters with their default, min, and max values
let shaderParams = {
    // Global parameters
    uUVvalue: 37.25,            // UV offset (min: 0.1, max: 100)
    Amplitude: 0.1821953,       // Noise amplitude (min: 0.0, max: 1.0)
    Hvalue: 0.062140625,        // Base noise value (min: 0.0, max: 1.0)
    Hvalue2: 0.0,               // Secondary noise value (min: 0.0, max: 1.0)
    iSpeed: 0.13751952,         // Animation speed (min: 0.0, max: 2.5)
    iAlpha: 1.0,                // Overall opacity (min: 0.0, max: 1.0)
    showGrid: 0.0,              // Show debug grid (0.0 = off, 1.0 = on)
    
    // Interaction parameters
    enableMouseScale: true,    // Toggle mouse movement scaling effect (default: off)

    // Layer 1 parameters
    Layer1CloudColour: [0.10616406, 0.6959453, 1.0], // Layer 1 color
    L1MoValue: 0.75,            // Layer 1 mouse influence (min: 0.0, max: 30.0)
    L1Scale: 0.26999998,        // Layer 1 scale (min: 0.0, max: 15.0)
    L1Turbulance: 0.0,          // Layer 1 turbulence (min: 0.0, max: 15.0)
    L1Zoom: 0.02440625,         // Layer 1 zoom (min: 0.0, max: 1.0)
    L1SmoothStep: [0.10307813, 0.6721953], // Layer 1 smoothstep (min: 0.0, max: 1.0)
    
    // Layer 2 parameters
    Layer2CloudColour: [1.0, 1.0, 1.0], // Layer 2 color
    L2MoValue: 0.0,             // Layer 2 mouse influence (min: 0.0, max: 1.0)
    L2Scale: 2.2027345,         // Layer 2 scale (min: 0.0, max: 50.0)
    L2Turbulance: 0.7597656,    // Layer 2 turbulence (min: 0.0, max: 50.0)
    L2Zoom: 0.0,                // Layer 2 zoom (min: 0.0, max: 1.0)
    L2SmoothStep: [0.35214844, 0.85415626], // Layer 2 smoothstep (min: 0.0, max: 1.0)
  };
  
  // M2-Compatible fragment shader code that uses sine-based noise instead of traditional noise
  const fragmentShader = `
  #ifdef GL_ES
  precision mediump float;
  #endif
  
  // Core uniforms
  uniform vec2 iResolution;      // Canvas size
  uniform float iTime;           // Time in seconds
  uniform vec4 iMouse;           // Mouse position
  
  // Global effect parameters
  uniform float uUVvalue;        // UV offset
  uniform float Amplitude;       // Noise amplitude
  uniform float Hvalue;          // Base noise value
  uniform float Hvalue2;         // Secondary noise value
  uniform float iSpeed;          // Animation speed
  uniform float iAlpha;          // Overall opacity
  uniform float showGrid;        // Show debug grid
  
  // Layer 1 parameters
  uniform vec3 Layer1CloudColour; // Layer 1 cloud color
  uniform float L1MoValue;       // Layer 1 mouse influence
  uniform float L1Scale;         // Layer 1 scale
  uniform float L1Turbulance;    // Layer 1 turbulence
  uniform float L1Zoom;          // Layer 1 zoom
  uniform vec2 L1SmoothStep;     // Layer 1 smoothstep range
  
  // Layer 2 parameters
  uniform vec3 Layer2CloudColour; // Layer 2 color
  uniform float L2MoValue;       // Layer 2 mouse influence
  uniform float L2Scale;         // Layer 2 scale
  uniform float L2Turbulance;    // Layer 2 turbulence
  uniform float L2Zoom;          // Layer 2 zoom
  uniform vec2 L2SmoothStep;     // Layer 2 smoothstep range
  
  // M2-compatible sine-based noise function with more complexity and variation
  float sineNoise(vec2 p) {
      // Add more variation to avoid circular patterns
      float n1 = sin(p.x * 1.27 + p.y * 0.97);
      float n2 = sin(p.y * 1.83 + p.x * 0.53);
      float n3 = sin((p.x + p.y) * 0.57);
      float n4 = sin((p.x * 0.73 - p.y * 1.21) * 1.13); // Add asymmetric component
      float n5 = sin((p.x * p.x * 0.1 + p.y * 0.3) * 0.77); // Add non-linear component
      
      // Combine for more organic, less circular look
      return 0.5 + 0.5 * (n1 * n2 * n3 + n4 * n5) * 0.5;
  }
  
  // More varied FBM using sine-based noise
  float fbm(vec2 p, float baseFreq, float baseAmp) {
      // Starting values
      float result = Hvalue;
      float amplitude = max(baseAmp, 0.01);
      float frequency = max(baseFreq, 0.01);
      
      // Use different rotation angles for each octave to avoid repetition
      mat2 rot1 = mat2(cos(0.5), sin(0.5), -sin(0.5), cos(0.5));
      mat2 rot2 = mat2(cos(0.7), sin(0.7), -sin(0.7), cos(0.7));
      mat2 rot3 = mat2(cos(1.1), sin(1.1), -sin(1.1), cos(1.1));
      
      // FBM summation with variable rotations
      float n = sineNoise(p * frequency);
      result += amplitude * n;
      amplitude *= 0.5;
      frequency *= 2.1; // Use non-integer multiplier to avoid patterns
      p = rot1 * p;
      
      n = sineNoise(p * frequency);
      result += amplitude * n;
      amplitude *= 0.5;
      frequency *= 1.9;
      p = rot2 * p;
      
      n = sineNoise(p * frequency);
      result += amplitude * n;
      amplitude *= 0.5;
      frequency *= 2.2;
      p = rot3 * p;
      
      n = sineNoise(p * frequency);
      result += amplitude * n;
      
      return 0.5 + 0.5 * result; // Normalize to 0-1 range
  }
  
  // Improved smoothstep for better transitions
  float smootherstep(float edge0, float edge1, float x) {
      x = clamp((x - edge0) / (edge1 - edge0), 0.0, 1.0);
      return x * x * x * (x * (x * 6.0 - 15.0) + 10.0);
  }
  
  // Scale-adaptive function to adjust animation based on zoom level
  vec2 getScaledAnimation(float zoomLevel, float speed, float time) {
      // Base animation speed
      float baseSpeed = speed * 0.5;
      
      // Reduce relative animation speed as zoom increases
      float scaleFactor = 500.0 / max(40.0, 1.0);
      
      // Create more varied, scale-appropriate animation
      return vec2(
          0.3 * sin(time * baseSpeed) * scaleFactor, 
          0.4 * cos(time * baseSpeed * 1.3) * scaleFactor
      );
  }
  
  void main() {
      // Set default values with appropriate clamping
      float uvScale = max(uUVvalue, 0.1);
      float amplitude = clamp(Amplitude, 0.0, 1.0);
      float speed = max(iSpeed, 0.0001);
      float alpha = max(iAlpha, 0.0);
      
      vec3 layer1Color = Layer1CloudColour;
      float layer1Scale = max(L1Scale, 0.01);
      float layer1Turbulence = max(L1Turbulance, 0.0);
      vec2 layer1SmoothStepRange = clamp(L1SmoothStep, vec2(0.0), vec2(1.0));
      
      vec3 layer2Color = Layer2CloudColour;
      float layer2Scale = max(L2Scale, 0.01);
      float layer2Turbulence = max(L2Turbulance, 0.0);
      vec2 layer2SmoothStepRange = clamp(L2SmoothStep, vec2(0.0), vec2(1.0));
      
      // Get normalized UV coordinates
      vec2 uv = gl_FragCoord.xy / iResolution.xy;
      
      // Apply proper UV transformation
      float aspect = iResolution.x / iResolution.y;
      vec2 centeredUV = (uv - 0.5) * 2.0;  // Center at origin and scale to -1 to 1
      centeredUV.x *= aspect;              // Apply aspect ratio correction
      centeredUV *= uvScale * 0.05;        // Scale by UV value
      
      // Get normalized mouse position
      vec2 mo = iMouse.xy / iResolution.xy;
      
      // Create scale-adaptive animation based on UV scale
      vec2 anim1 = getScaledAnimation(uvScale, speed, iTime);
      vec2 anim2 = getScaledAnimation(uvScale, speed * 0.7, iTime);
      
      // Layer 1 (foreground clouds)
      vec2 layer1UV = centeredUV * layer1Scale;
      
      // Apply mouse influence - scaled based on zoom level
      float mouseScaleFactor = 0.2 * (15.0 / max(uvScale, 1.0));
      layer1UV += (mo - 0.5) * L1MoValue * mouseScaleFactor;
      
      // Apply turbulence
      float turbScale1 = layer1Turbulence * (15.0 / max(uvScale, 1.0));
      vec2 turbulence1 = turbScale1 * vec2(
          sineNoise(centeredUV * 10.0),
          sineNoise(centeredUV * 10.0 + vec2(5.0, 3.0))
      );
      layer1UV += turbulence1;
      
      // Generate animated noise for layer 1
      float noise1 = fbm(
          layer1UV + anim1,
          1.0,
          amplitude
      );
      
      // Layer 2 (background clouds)
      vec2 layer2UV = centeredUV * layer2Scale;
      
      // Apply turbulence
      float turbScale2 = layer2Turbulence * (15.0 / max(uvScale, 1.0));
      vec2 turbulence2 = turbScale2 * vec2(
          sineNoise(centeredUV * 5.0),
          sineNoise(centeredUV * 2.1 + vec2(1.0, 2.0))
      );
      layer2UV += turbulence2;
      
      // Generate animated noise for layer 2
      float noise2 = fbm(
          layer2UV + anim2,
          1.0,
          amplitude
      );
      
      // Base sky color
      vec3 sky = vec3(1.0);
      vec3 finalColor = vec3(0.0);
      
      // Mix layers with smootherstep for better transitions
      finalColor = mix(sky, layer1Color, smootherstep(
          layer1SmoothStepRange.x, layer1SmoothStepRange.y, noise1
      ));
      finalColor = mix(finalColor, layer2Color, smootherstep(
          layer2SmoothStepRange.x, layer2SmoothStepRange.y, noise2
      ));
      
      // Ensure colors stay in bounds
      finalColor = min(finalColor, vec3(1.0));
      
      // Add debug grid if enabled
      if (showGrid > 0.5) {
          vec2 grid = fract(centeredUV);
          if (grid.x < 0.03 || grid.x > 0.97 || grid.y < 0.03 || grid.y > 0.97) {
              finalColor = mix(finalColor, vec3(1.0, 1.0, 1.0), 0.2);
          }
      }
      
      // Output with full opacity (no edge feathering)
      gl_FragColor = vec4(finalColor, alpha);
  }
  `;
  
  // Simple vertex shader that passes through the position and texture coordinates
  const vertexShader = `
  attribute vec3 aPosition;
  attribute vec2 aTexCoord;
  
  void main() {
    vec4 positionVec4 = vec4(aPosition, 1.0);
    positionVec4.xy = positionVec4.xy * 2.0 - 1.0;
    gl_Position = positionVec4;
  }
  `;
  
  // Set up the dat.GUI controls
  function setupGui() {
    gui = new dat.GUI();
    
    // Create folders for better organization
    let globalParams = gui.addFolder('Global Parameters');
    let interactionParams = gui.addFolder('Interaction Controls');
    let layer1Params = gui.addFolder('Layer 1 Parameters');
    let layer2Params = gui.addFolder('Layer 2 Parameters');
    
    // Global Parameters
    globalParams.add(shaderParams, 'uUVvalue', 0.1, 100.0).name('UV Scale');
    globalParams.add(shaderParams, 'Amplitude', 0.0, 1.0).name('Noise Amplitude');
    globalParams.add(shaderParams, 'Hvalue', 0.0, 1.0).name('Base Noise Value');
    globalParams.add(shaderParams, 'iSpeed', 0.0, 2.5).name('Animation Speed');
    globalParams.add(shaderParams, 'iAlpha', 0.0, 1.0).name('Opacity');
    globalParams.add(shaderParams, 'showGrid', 0, 1).name('Show Grid').step(1);
    
    // Interaction Parameters
    interactionParams.add(shaderParams, 'enableMouseScale').name('Mouse Scale');
    
    // Layer 1 Parameters
    layer1Params.addColor(shaderParams, 'Layer1CloudColour').name('Cloud Color');
    layer1Params.add(shaderParams, 'L1MoValue', 0.0, 30.0).name('Mouse Influence');
    layer1Params.add(shaderParams, 'L1Scale', 0.0, 15.0).name('Scale');
    layer1Params.add(shaderParams, 'L1Turbulance', 0.0, 15.0).name('Turbulence');
    layer1Params.add(shaderParams, 'L1Zoom', 0.0, 1.0).name('Zoom');
    
    // Layer 2 Parameters
    layer2Params.addColor(shaderParams, 'Layer2CloudColour').name('Cloud Color');
    layer2Params.add(shaderParams, 'L2Scale', 0.0, 50.0).name('Scale');
    layer2Params.add(shaderParams, 'L2Turbulance', 0.0, 50.0).name('Turbulence');
    layer2Params.add(shaderParams, 'L2Zoom', 0.0, 1.0).name('Zoom');
    
    // Open global parameters by default
    globalParams.open();
    interactionParams.open();
    
    // Close the GUI by default for a cleaner interface
    gui.close();
  }